import { memo, useMemo } from "react";
import { Alert, Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import HistoryIcon from "@mui/icons-material/History";
import { toIpfsGatewayUrl } from "../utils/ipfs";

function formatBidEth(value) {
  if (value == null) return "0.00";
  return (Number(value) / 1e18).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatTimeLeft(endTime, now) {
  if (!endTime) return "00:00";
  const diff = Math.max(0, Number(endTime) - Number(now || Math.floor(Date.now() / 1000)));
  const minutes = Math.floor(diff / 60)
    .toString()
    .padStart(2, "0");
  const seconds = (diff % 60).toString().padStart(2, "0");
  return `${minutes}:${seconds}`;
}

function MarketplaceItem({
  nft,
  now,
  currentAccount,
  onRequestList,
  onCancel,
  onBuy,
  onRequestBid,
  onFinalizeAuction,
  onRequestBidHistory,
  onRequestAuditHistory,
  onRequestMetadata,
}) {
  const theme = useTheme();

  const auctionData = nft?.auctionData || null;
  const pendingOperation = nft?.pendingOperation || null;
  const effectiveNow = Number(now || Math.floor(Date.now() / 1000));

  const isAuctionEnded = Boolean(auctionData?.endTime && Number(auctionData.endTime) < effectiveNow);
  const isAuctionActive = Boolean(auctionData?.active && !isAuctionEnded);
  const hasAuctionContext = Boolean(
    nft?.isAuction ||
      auctionData?.active ||
      (auctionData?.endTime && Number(auctionData.endTime) > 0) ||
      (auctionData?.minPrice && Number(auctionData.minPrice) > 0) ||
      (auctionData?.highestBid && Number(auctionData.highestBid) > 0) ||
      (auctionData?.auctionStartBlock && Number(auctionData.auctionStartBlock) > 0)
  );
  const timeLeft = useMemo(
    () => formatTimeLeft(auctionData?.endTime, effectiveNow),
    [auctionData?.endTime, effectiveNow]
  );

  const imageUrl = useMemo(() => toIpfsGatewayUrl(nft?.image || ""), [nft?.image]);
  const isOwner = Boolean(
    currentAccount &&
      nft?.owner &&
      String(currentAccount).toLowerCase() === String(nft.owner).toLowerCase()
  );

  return (
    <div
      className="minted-nft-card"
      style={{
        flex: "0 0 auto",
        width: "240px",
        minHeight: "400px",
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${theme.palette.divider}`,
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
        transition: "box-shadow 0.2s, transform 0.2s",
        cursor: "pointer",
        scrollSnapAlign: "start",
        textAlign: "center",
        boxShadow: theme.shadows[1],
        padding: "12px",
        position: "relative",
      }}
      onMouseEnter={(event) => {
        event.currentTarget.style.boxShadow = theme.shadows[4];
      }}
      onMouseLeave={(event) => {
        event.currentTarget.style.boxShadow = theme.shadows[1];
      }}
    >
      {hasAuctionContext && (
        <Box
          sx={{
            position: "absolute",
            top: 8,
            left: 8,
            backgroundColor:
              theme.palette.mode === "dark" ? "rgba(0, 0, 0, 0.85)" : "rgba(255, 255, 255, 0.95)",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.shape.borderRadius,
            padding: theme.spacing(0.5, 1),
            zIndex: 2,
            maxWidth: "calc(100% - 44px)",
          }}
        >
          {isAuctionEnded ? (
            <Typography variant="body2" sx={{ fontSize: "0.7rem", fontWeight: 600, color: theme.palette.error.main }}>
              {auctionData.highestBid && Number(auctionData.highestBid) > 0
                ? `Won: ${formatBidEth(auctionData.highestBid)} ETH`
                : "No bids"}
            </Typography>
          ) : (
            <Typography variant="caption" sx={{ fontSize: "0.7rem", fontWeight: 600, color: theme.palette.warning.main }}>
              {auctionData.highestBid && Number(auctionData.highestBid) > 0
                ? `Bid: ${formatBidEth(auctionData.highestBid)} ETH`
                : `Start: ${auctionData.minPrice ? formatBidEth(auctionData.minPrice) : "?"} ETH`}
            </Typography>
          )}
        </Box>
      )}

      {hasAuctionContext ? (
        <Tooltip title="View bidding history" arrow>
          <IconButton
            onClick={(event) => {
              event.stopPropagation();
              onRequestBidHistory(nft);
            }}
            size="large"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              backgroundColor: theme.palette.background.default,
              border: `1px solid ${theme.palette.divider}`,
              width: 38,
              height: 38,
              zIndex: 2,
            }}
          >
            <HistoryIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ) : null}

      <h2 style={{ fontSize: "1rem", fontWeight: 600, marginBottom: "8px" }}>#{nft.tokenId}</h2>
      <img
        src={imageUrl}
        alt={`NFT ${nft.tokenId}`}
        loading="lazy"
        decoding="async"
        style={{
          width: "100%",
          height: "260px",
          objectFit: "cover",
          borderRadius: "8px",
          marginBottom: "8px",
        }}
        onClick={() => onRequestAuditHistory(nft)}
      />

      {nft.retired ? (
        <Alert severity="success" onClick={() => onRequestMetadata(nft)} sx={{ mt: 1, py: 1.5 }}>
          <Typography variant="body2" fontWeight={600} fontSize="0.75rem">
            RETIRED
          </Typography>
        </Alert>
      ) : isAuctionEnded ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 1 }}>
          {auctionData?.active ? (
            <Button
              variant="contained"
              color="warning"
              size="small"
              onClick={() => onFinalizeAuction(nft.tokenId)}
              disabled={pendingOperation === "finalizing"}
              sx={{ fontSize: "0.75rem" }}
            >
              {pendingOperation === "finalizing" ? "Finalizing..." : "Finalize Auction"}
            </Button>
          ) : (
            <Box sx={{ height: "32px" }} />
          )}

          <Typography variant="caption" color="error" fontWeight={600} sx={{ textAlign: "center", mt: 0.5 }}>
            Auction Ended
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 1 }}>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            {!isOwner && nft.isListed && !nft.isAuction && !isAuctionActive && (
              <Button
                onClick={() => onBuy(nft.tokenId, nft.price)}
                variant="contained"
                color="primary"
                size="small"
                disabled={pendingOperation === "buying"}
                sx={{ flex: 1, fontSize: "0.75rem", minWidth: "auto" }}
              >
                {pendingOperation === "buying" ? "Buying..." : "Buy"}
              </Button>
            )}

            {!isOwner && (nft.isAuction || isAuctionActive) && (
              <Button
                onClick={() => onRequestBid(nft)}
                variant="contained"
                color="warning"
                size="small"
                disabled={pendingOperation === "bidding"}
                sx={{ flex: 1, fontSize: "0.75rem", minWidth: "auto" }}
              >
                {pendingOperation === "bidding" ? "Placing..." : "Bid"}
              </Button>
            )}

            {isOwner && nft.isListed && !nft.isAuction && (
              <Button
                onClick={() => onCancel(nft.tokenId)}
                variant="contained"
                color="secondary"
                size="small"
                disabled={pendingOperation === "cancelling"}
                sx={{ flex: 1, fontSize: "0.75rem", minWidth: "auto" }}
              >
                {pendingOperation === "cancelling" ? "Cancelling..." : "Cancel"}
              </Button>
            )}

            {isOwner && !nft.isListed && (
              <Button
                onClick={() => onRequestList(nft)}
                variant="contained"
                color="primary"
                disabled={pendingOperation === "listing"}
                size="small"
                sx={{ flex: 1, fontSize: "0.75rem", minWidth: "auto" }}
              >
                {pendingOperation === "listing" ? "Listing..." : "List"}
              </Button>
            )}

            <Button
              onClick={() => onRequestMetadata(nft)}
              variant="outlined"
              color="info"
              size="small"
              sx={{ flex: 1, fontSize: "0.75rem", minWidth: "auto" }}
            >
              Metadata
            </Button>
          </Box>

          {nft.isListed && !nft.isAuction && (
            <Typography variant="caption" color="primary" fontSize="0.7rem" fontWeight={600} sx={{ textAlign: "center", mt: 0.5 }}>
              {isOwner ? "Listed: " : "Price: "}
              {(Number(nft.price || 0) / 1e18).toFixed(4)} ETH
            </Typography>
          )}

          {isAuctionActive && (
            <Typography variant="caption" color="warning" sx={{ fontSize: "0.7rem", textAlign: "center" }}>
              Ends in: {timeLeft}
            </Typography>
          )}
        </Box>
      )}
    </div>
  );
}

export default memo(MarketplaceItem);
